import { Divider, Grid, OutlinedTextFieldProps, Paper, TextField, Typography, styled } from "@mui/material";
import React, { forwardRef, useContext, useEffect, useState } from "react";

import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { EnhancedCustomer } from "../../Models/Customer";
import { DateTime } from "../../Types/DateTime";
import { emailIsValid, zipCodeIsValid } from "../../Utils/Helpers";
import {
  emailValidation,
  firstNameValidation,
  heightFeetValidation,
  heightInchesValidation,
  lastNameValidation,
  weightValidation
} from "../../Utils/ValidationSchema";
import EnvironmentConfiguration from "../../V2/Constants/EnvironmentConfiguration";
import { UserContextCenterSelector } from "../../V2/Ribbon/Compositions/SessionContextCenterSelector";
import DatePicker from "../Helpers/DatePicker";
import RibbonPhoneNumberField from "./RibbonPhoneNumberTextField";
import RibbonValidatedTextField from "./RibbonValidatedTextField";

const Ribbon: React.FC = () => {
  const {
    customer,
    duplicateCustomers,
    lead,
    previewCustomer,
    selectedCustomer,
    step,
    unverifiedCustomer,
    setCustomer,
    setUnverifiedCustomer
  } = useContext(SessionContext);
  const { centers, leadResultTypes } = useContext(UserContext);

  const [originalCustomer, setOriginalCustomer] = useState(new EnhancedCustomer());
  const [ribbonPreview, setRibbonPreview] = useState<EnhancedCustomer>();

  // on lead or preview customer changing update the customer preview
  useEffect(
    () =>
      setRibbonPreview(
        new EnhancedCustomer({
          ...previewCustomer,
          firstName: previewCustomer?.firstName || lead.firstName,
          lastName: previewCustomer?.lastName || lead.lastName,
          phoneNumber: previewCustomer?.phoneNumber || lead.phoneNumber,
          email: previewCustomer?.email || lead.email,
          zipCode: previewCustomer?.zipCode || lead.zipCode
        })
      ),
    [lead, previewCustomer]
  );

  // on lead or selected customer changing, maintain the initial customer state used in red-dot switching
  useEffect(() => {
    const { firstName, lastName, phoneNumber, email, centerId, zipCode } = lead;
    const newOriginalCustomer = new EnhancedCustomer({
      firstName,
      lastName,
      phoneNumber,
      email,
      centerId,
      zipCode,
      center: centers.find(c => c.id === centerId)
    });
    if (selectedCustomer)
      Object.assign(newOriginalCustomer, Object.fromEntries(Object.entries(selectedCustomer).filter(([, v]) => !!v)));
    setOriginalCustomer(newOriginalCustomer);
  }, [lead, selectedCustomer]);
  const { feet, inches, ...rest } = unverifiedCustomer;
  const ribbonCustomer = (step.isPreviewCustomerShown && ribbonPreview) || {
    ...customer,
    ...rest,
    height: { feet, inches }
  };
  const customerUrl = ribbonCustomer.externalId
    ? `${process.env.REACT_APP_CRM_BASEURL}/Guests/GuestProfile.aspx?UserId=${ribbonCustomer.externalId}`
    : "";
  const opportunityUrl = ribbonCustomer.opportunity?.externalId
    ? `${process.env.REACT_APP_CRM_BASEURL}/Admin/Sales/OpportunityCreate.aspx?OpportunityId=${ribbonCustomer.opportunity.externalId}`
    : "";
  const leadResultType =
    (!!ribbonCustomer.opportunity?.leadResult &&
      leadResultTypes.find(v => v.id === ribbonCustomer?.opportunity?.leadResult?.type)) ||
    undefined;
  const phoneDuplicateError =
    duplicateCustomers.some(c => c.phoneNumber === ribbonCustomer.phoneNumber) && "Number is a duplicate.";
  return (
    <Paper sx={{ p: 1 }}>
      <Grid container direction="column" spacing={1}>
        <Grid container item justifyContent="space-around" spacing={1}>
          <Grid item xs>
            <RibbonValidatedTextField
              label="First Name"
              value={ribbonCustomer.firstName || ""}
              revertValue={originalCustomer?.firstName || undefined}
              error={duplicateCustomers.some(c => c.firstName === ribbonCustomer.firstName)}
              onChange={firstName => setUnverifiedCustomer(c => ({ ...c, firstName: firstName || "" }))}
              onAccepted={firstName => setCustomer(c => ({ ...c, firstName: firstName || null }))}
              disabled={step.isPreviewCustomerShown}
              {...firstNameValidation}
            />
          </Grid>
          <Grid item xs>
            <RibbonValidatedTextField
              label="Last Name"
              value={ribbonCustomer.lastName || ""}
              revertValue={originalCustomer?.lastName ?? undefined}
              error={duplicateCustomers.some(c => c.lastName === ribbonCustomer.lastName)}
              onChange={lastName => setUnverifiedCustomer(c => ({ ...c, lastName: lastName || "" }))}
              onAccepted={lastName => setCustomer(c => ({ ...c, lastName: lastName || null }))}
              disabled={step.isPreviewCustomerShown}
              {...lastNameValidation}
            />
          </Grid>
          <Grid item xs>
            <RibbonPhoneNumberField
              customerKey="phoneNumber"
              label="Mobile Phone"
              error={Boolean(phoneDuplicateError)}
              helperText={phoneDuplicateError || undefined}
              value={ribbonCustomer.phoneNumber || ""}
              revertValue={originalCustomer?.phoneNumber ?? undefined}
              onAccepted={phoneNumber => setCustomer(c => ({ ...c, phoneNumber: phoneNumber || null }))}
              onChange={v => setUnverifiedCustomer(c => ({ ...c, phoneNumber: v || "" }))}
              onPaste={e =>
                setUnverifiedCustomer(c => ({
                  ...c,
                  phoneNumber: e.clipboardData.getData("text").replace(/[^0-9]/g, "")
                }))
              }
              disabled={step.isPreviewCustomerShown}
            />
          </Grid>
          <Grid item xs>
            <RibbonPhoneNumberField
              customerKey="homePhoneNumber"
              label="Home Phone"
              value={ribbonCustomer.homePhoneNumber || ""}
              revertValue={originalCustomer?.homePhoneNumber ?? undefined}
              onAccepted={homePhoneNumber => setCustomer(c => ({ ...c, homePhoneNumber: homePhoneNumber || null }))}
              onChange={v => setUnverifiedCustomer(c => ({ ...c, homePhoneNumber: v || "" }))}
              onPaste={e =>
                setUnverifiedCustomer(c => ({
                  ...c,
                  homePhoneNumber: e.clipboardData.getData("text").replace(/[^0-9]/g, "")
                }))
              }
              disabled={step.isPreviewCustomerShown}
            />
          </Grid>
          <Grid item xs>
            <RibbonPhoneNumberField
              customerKey="workPhoneNumber"
              label="Work Phone"
              value={ribbonCustomer.workPhoneNumber || ""}
              revertValue={originalCustomer?.workPhoneNumber ?? undefined}
              onAccepted={workPhoneNumber => setCustomer(c => ({ ...c, workPhoneNumber: workPhoneNumber || null }))}
              onChange={v => setUnverifiedCustomer(c => ({ ...c, workPhoneNumber: v || "" }))}
              onPaste={e =>
                setUnverifiedCustomer(c => ({
                  ...c,
                  workPhoneNumber: e.clipboardData.getData("text").replace(/[^0-9]/g, "")
                }))
              }
              disabled={step.isPreviewCustomerShown}
            />
          </Grid>
        </Grid>
        <Grid container item justifyContent="space-around" alignItems="center" spacing={1}>
          <Grid item xs>
            <RibbonValidatedTextField
              label="Email"
              error={
                duplicateCustomers.some(c => c.email === ribbonCustomer.email) ||
                (!!ribbonCustomer.email && !emailIsValid(ribbonCustomer.email))
              }
              value={ribbonCustomer.email || ""}
              revertValue={originalCustomer?.email ?? undefined}
              onChange={email => setUnverifiedCustomer(c => ({ ...c, email: email || "" }))}
              onAccepted={email => setCustomer(c => ({ ...c, email: email || null }))}
              disabled={step.isPreviewCustomerShown}
              {...emailValidation}
            />
          </Grid>
          <Grid item xs={1} sx={{ minWidth: "8rem" }}>
            <RibbonValidatedTextField
              label="Zip Code"
              error={!!ribbonCustomer.zipCode && !zipCodeIsValid(ribbonCustomer.zipCode)}
              value={ribbonCustomer.zipCode || ""}
              revertValue={originalCustomer?.zipCode ?? undefined}
              onChange={zipCode => setUnverifiedCustomer(c => ({ ...c, zipCode: zipCode || "" }))}
              onAccepted={zipCode => setCustomer(c => ({ ...c, zipCode: zipCode || null }))}
              disabled={step.isPreviewCustomerShown}
              debounce={1000}
            />
          </Grid>
          <Grid item xs sx={{ marginTop: -2.0 }}>
            <UserContextCenterSelector
              value={ribbonCustomer.center || null}
              onChange={center =>
                setCustomer(c => ({ ...c, center: center || undefined, centerId: center && center.id }))
              }
              disabled={step.isPreviewCustomerShown}
            />
          </Grid>
          <Grid container item xs alignItems="start" spacing={1}>
            <Grid item sx={{ marginTop: 0.8 }}>
              <Typography variant="body2">Height</Typography>
            </Grid>
            <Grid item xs>
              <RibbonValidatedTextField
                label="Feet"
                value={ribbonCustomer.height?.feet ?? ""}
                revertValue={originalCustomer.height?.feet ?? undefined}
                onChange={feet => setUnverifiedCustomer(c => ({ ...c, feet: feet ?? "" }))}
                onAccepted={feet =>
                  setCustomer(c => ({
                    ...c,
                    height: feet || c.height?.inches ? { inches: c.height?.inches ?? 0, feet: Number(feet ?? 0) } : null
                  }))
                }
                debounce={0}
                disabled={step.isPreviewCustomerShown}
                {...heightFeetValidation}
              />
            </Grid>
            <Grid item xs>
              <RibbonValidatedTextField
                label="Inches"
                value={ribbonCustomer.height?.inches ?? ""}
                revertValue={originalCustomer.height?.inches ?? undefined}
                debounce={300}
                onChange={inches => setUnverifiedCustomer(c => ({ ...c, inches: inches ?? "" }))}
                onAccepted={inches =>
                  setCustomer(c => ({
                    ...c,
                    height: c.height?.feet || inches ? { feet: c.height?.feet ?? 0, inches: Number(inches ?? 0) } : null
                  }))
                }
                disabled={step.isPreviewCustomerShown}
                {...heightInchesValidation}
              />
            </Grid>
          </Grid>
          <Grid item sx={{ width: "10.5rem" }}>
            <RibbonValidatedTextField
              label="Weight"
              value={ribbonCustomer.weight ?? ""}
              revertValue={originalCustomer.weight ?? undefined}
              onChange={weight => setUnverifiedCustomer(c => ({ ...c, weight: weight ?? "" }))}
              onAccepted={weight => setCustomer(c => ({ ...c, weight: weight ? Number(weight) : null }))}
              debounce={300}
              disabled={step.isPreviewCustomerShown}
              {...weightValidation}
            />
          </Grid>
          <Grid item sx={{ marginTop: -2.0 }}>
            <Typography variant="body2">{`BMI :  ${ribbonCustomer.bmi || "##.##"}`}</Typography>
          </Grid>
        </Grid>
        <Grid container item justifyContent="space-around" alignItems="center" spacing={3} wrap="nowrap">
          <Grid item xs>
            <DatePicker
              inputProps={{
                placeholder: "mm/dd/yyyy",
                sx: theme => ({
                  minWidth: "8.5rem",
                  maxWidth: "12rem",
                  "& .MuiOutlinedInput-notchedOutline":
                    Boolean(originalCustomer.dateOfBirth) !== Boolean(ribbonCustomer.dateOfBirth) ||
                    (originalCustomer.dateOfBirth &&
                      ribbonCustomer.dateOfBirth &&
                      DateTime.fromISO(originalCustomer.dateOfBirth).toISODate() !==
                        DateTime.fromISO(ribbonCustomer.dateOfBirth).toISODate())
                      ? { borderColor: theme.palette.secondary.main, borderWidth: ".15em" }
                      : undefined
                })
              }}
              label="Date of Birth"
              value={ribbonCustomer.dateOfBirth || ""}
              onChange={dateOfBirth => setUnverifiedCustomer(c => ({ ...c, dateOfBirth }))}
              onAccepted={date => setCustomer(c => ({ ...c, dateOfBirth: date?.toISO() || null }))}
              disabled={step.isPreviewCustomerShown}
            />
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Age :  ${
                ribbonCustomer.dateOfBirth
                  ? Math.floor(DateTime.now().diff(DateTime.fromISO(ribbonCustomer.dateOfBirth), "years").years)
                  : "##"
              }`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {"Guest Code :  "}
              {customerUrl ? (
                <a href={customerUrl} target="_blank" rel="noopener noreferrer">
                  {ribbonCustomer.code || "No Code"}
                </a>
              ) : (
                "No Customer"
              )}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {"Opportunity Number :  "}
              {opportunityUrl && ribbonCustomer.opportunity ? (
                // eslint-disable-next-line
                <a href={opportunityUrl} target="_blank">
                  {ribbonCustomer.opportunity.number || "No Number"}
                </a>
              ) : (
                "No Opportunity"
              )}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Prior Disposition :  ${leadResultType?.name || "None"}`}
            </Typography>
          </Grid>
          <Grid item sx={{ mx: -1 }}>
            <Divider sx={{ height: "2rem" }} orientation="vertical" flexItem />
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Campaign ID :  ${lead.campaign.code || "None"}`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Category :  ${lead.campaign.optionalField2 || "None"}`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Parent :  ${lead.campaign.optionalField3 || "None"}`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" align="center">
              {`Call to Action :  ${lead.campaign.optionalField4 || "None"}`}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export interface PhoneFieldProps extends Partial<OutlinedTextFieldProps> {
  /** Custom DOM prop used to pass the error flag to the component. Lowercase scheme and string type are required. */
  haserror: "true" | "false";
}
export const PhoneField = styled(TextField, {
  shouldForwardProp: prop => prop !== "haserror"
})<PhoneFieldProps>(({ haserror, theme }) => ({
  "& .MuiOutlinedInput-notchedOutline":
    haserror === "true" ? { borderColor: theme.palette.error.main, borderWidth: ".15em" } : undefined
}));

const PhoneInput = forwardRef((props: PhoneFieldProps, ref) => (
  <PhoneField variant="outlined" {...props} inputRef={ref} type="string" fullWidth />
));
PhoneInput.displayName = "PhoneInput";
const HomePhoneInput = forwardRef((props: PhoneFieldProps, ref) => (
  <PhoneField variant="outlined" {...props} inputRef={ref} type="string" fullWidth />
));
HomePhoneInput.displayName = "HomePhoneInput";
const WorkPhoneInput = forwardRef((props: PhoneFieldProps, ref) => (
  <PhoneField variant="outlined" {...props} inputRef={ref} type="string" fullWidth />
));
WorkPhoneInput.displayName = "WorkPhoneInput";

export default Ribbon;
